import * as moment from 'moment';
import _ from 'lodash';
import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { filter } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { VerificateOrg } from '../../features/data/data.actions';
import { selectVereficationOrgError } from '../../features/data/data.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-signup04',
  templateUrl: './signup04.component.html',
  styleUrls: ['./signup04.component.scss']
})
export class Signup04Component implements OnInit {
  destroyRef = inject(DestroyRef)
  @ViewChild('field', {static: true}) field: ElementRef;
  
  public error$ = this.store.pipe(
    select(selectVereficationOrgError)
  );

  public form: UntypedFormGroup = new UntypedFormGroup({
    addressLine1: new UntypedFormControl('', [Validators.required]),
    addressLine2: new UntypedFormControl(''),
    addressCity: new UntypedFormControl('', [Validators.required]),
    addressZipCode: new UntypedFormControl('', [Validators.required]),
    addressState: new UntypedFormControl('', [Validators.required]),
    url: new UntypedFormControl('', [Validators.required, this.urlValidator()]),
    orgTaxId: new UntypedFormControl('', [Validators.required]),
    ARLFirstName: new UntypedFormControl('', [Validators.required]),
    ARLastName: new UntypedFormControl('', [Validators.required]),
    ArSSNLast4: new UntypedFormControl('', [Validators.required]),
    DOBday: new UntypedFormControl(null, [Validators.required]),
    DOBMonth: new UntypedFormControl(null, [Validators.required]),
    DOBYear: new UntypedFormControl(null, [Validators.required])
  });
  public submitted: boolean = false;

  public addressControl: UntypedFormControl = new UntypedFormControl(null);
  public address: string = '';
  public addressObject: any = null;
  public apt: UntypedFormControl = new UntypedFormControl('');

  public datepicker: UntypedFormControl = new UntypedFormControl(null, [Validators.required]);
  public datepickerRequired = true;
  public myDatePickerOptions: any = {
    dateFormat: 'mm/dd/yyyy'
  }
  public selectedDate = null;

  constructor(
    private store: Store
  ) {
    const date = moment();
    const year = date.year();
    const maxYear = year - 18;
    const minYear = maxYear - 100;
    this.myDatePickerOptions = {
      dateFormat: 'mm/dd/yyyy',
      maxYear, minYear
    }

    date.year(maxYear);
    this.selectedDate = {
      date: {
        year: date.year(),
        month: date.month() + 1,
        day: date.date()
      }
    }

    const dateUntil = moment().year(minYear).subtract(1, 'days');
    this.myDatePickerOptions.disableUntil = { year: dateUntil.year(), month: dateUntil.month() + 1, day: dateUntil.date() };
    const dateSince = moment().year(maxYear).add(1, 'days');
    this.myDatePickerOptions.disableSince = { year: dateSince.year(), month: dateSince.month() + 1, day: dateSince.date() };
  }

  ngOnInit(): void {
    this.form.get('ArSSNLast4').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((value) => {
      const length = value.split('').length;

      if(length > 4) {
        const sliced = value.slice(0, 4);
        this.form.get('ArSSNLast4').setValue(sliced);
      }
    })

    this.error$.pipe(
      filter(error => !_.isNull(error)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ errorCode }) => {
      switch(errorCode) {
        case 'InvalidTaxId':
          this.form.get('orgTaxId').setErrors({ invalid: true });
          break;
        case 'InvalidLast4SSN':
          this.form.get('ArSSNLast4').setErrors({ invalid: true});
          break;
        case 'InvalidUrl':
          this.form.get('url').setErrors({ invalid: true});
          break;
      }
    });

    this.datepicker.valueChanges.pipe(
      filter(value => !_.isUndefined(value)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      const { form } = this;

      const unix = moment(value, 'MM/DD/YYYY');
      const date = unix.date();
      const month = unix.month() + 1;
      const year = unix.year();

      form.get('DOBday').setValue(date);
      form.get('DOBMonth').setValue(month);
      form.get('DOBYear').setValue(year);
    });

    this.addressControl.valueChanges.pipe(
      filter(value => value !== null),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ street1, street2, city, zip, state }) => {
      this.form.get('addressLine1').setValue(street1);
      this.form.get('addressLine2').setValue(street2);
      this.form.get('addressCity').setValue(city);
      this.form.get('addressZipCode').setValue(zip);
      this.form.get('addressState').setValue(state);
    });

    this.addressControl.valueChanges.pipe(
      filter(value => value === null),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.form.get('addressLine1').setValue(null);
      this.form.get('addressLine2').setValue(null);
      this.form.get('addressCity').setValue(null);
      this.form.get('addressZipCode').setValue(null);
      this.form.get('addressState').setValue(null);
    });
  }

  toggleClassActive(val,inputMdb){

    if(val){
      inputMdb?.classList.add('active')
    }else{
      inputMdb?.classList.remove('active')
    }
  }
  
  urlValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const urlPattern = /^(?:(?:http(s)?):\/\/(www\.)?)([-a-z0-9+&@#\/%?=~_|!:,.;])*([-a-z0-9+&@#\/%=~_|])$/i;
      const hostPattern = /^(www\.)?([a-z]+)$/i;
      
      if (!urlPattern.test(control.value)) {
        return { 'pattern': true };
      }
  
      let url = null;
      try {
        url = new URL (control.value);
      } catch(e) {
        return { 'pattern': true };
      }

      if (url.host === 'http' ) {
        return { 'pattern': true };
      }

      if(url.host === 'https') {
        return { 'pattern': true };
      }

      if (url.hostname === 'http' ) {
        return { 'pattern': true };
      }

      if(url.hostname === 'https') {
        return { 'pattern': true };
      }

      if(hostPattern.test(url.host)) {
        return { 'pattern': true };
      }

      if(hostPattern.test(url.hostname)) {
        return { 'pattern': true };
      }

      return null;
    };
  }

  getAddressErrorMessage() {
    const { form } = this;
    const addressLine1 = form.get('addressLine1');
    const addressCity = form.get('addressCity');
    const addressZipCode = form.get('addressZipCode');
    const addressState = form.get('addressState');

    if(!addressLine1.touched) return '';
    if(!addressCity.touched) return '';
    if(!addressZipCode.touched) return '';
    if(!addressState.touched) return '';

    if(addressLine1.hasError('required')
      && addressCity.hasError('required')
      && addressZipCode.hasError('required')
      && addressState.hasError('required')
    ) {
      return 'Please provide complete address, select from suggested.';
    }

    if(addressLine1.hasError('required')) {
      return 'Incorrect Address Line';
    }

    if(addressCity.hasError('required')) {
      return 'Incorrect City';
    }

    if(addressZipCode.hasError('required')) {
      return 'Incorrect Zip Code';
    }

    if(addressState.hasError('required')) {
      return 'Incorrect State';
    }

    return '';
  }

  getWebsiteErrorMessage() {
    const { form } = this;
    const url = form.get('url');

    if(!url.touched) return '';

    if(url.hasError('required')) {
      return 'Please provide Website URL';
    }

    if(url.hasError('pattern')) {
      return 'Incorrect Website URL';
    }

    if(url.hasError('invalid')) {
      return 'Incorrect Website URL';
    }

   return '';
  }

  getTaxIdErrorMessage() {
    const { form } = this;
    const orgTaxId = form.get('orgTaxId');

    if(!orgTaxId.touched) return '';

    if(orgTaxId.hasError('required')) {
      return 'Please provide Tax ID';
    }

    if(orgTaxId.hasError('invalid')) {
      return 'Invalid Tax Id';
    }

    return '';
  }

  getLast4SSNErrorMessage() {
    const { form } = this;
    const arSSNLast4 = form.get('ArSSNLast4');

    if(!arSSNLast4.touched) return '';

    if(arSSNLast4.hasError('required')) {
      return 'Please provide Last 4 Digits of SSN';
    }

    if(arSSNLast4.hasError('invalid')) {
      return 'Invalid Last 4 Digits of SSN';
    }

    return '';
  }

  onBlurURL() {
    const url = this.form.get("url");
    if(url.value.search(/(?:http(s)?):\/\//) < 0) {
      url.setValue(`http://${url.value}`);
    }
  }

  onNext() {
    const { form, apt } = this;
    form.markAllAsTouched();
    this.submitted = true;

    if(_.isEmpty(apt.value) === false) {
      const addressLine1 = this.form.get('addressLine1');
      addressLine1.setValue(`${addressLine1.value} #${apt.value}`);
    }

    if(form.valid) {
      this.store.dispatch({
        type: VerificateOrg.type,
        payload: form.value
      });
    }
  }

}
